// Save one HR form without losing work in another task editor.
//
// SPA (2026-09-10): 인사 화면은 사이드바 이동을 제자리 교체로 처리한다
// (calendar-spa.js 의 `hr` 섹션 + 페이지 컨테이너의 .hr-page 표식). 교체가 끝나면
// #hrOperations(=main.admin-main)는 새 노드이므로 이 모듈은 다시 붙어야 한다 —
// 그래서 문서·창 리스너는 모듈 전역에서 딱 한 번만 걸고(wireOnce), 상태(현재 루트·
// 라벨·baseline)는 모듈 스코프에 두고 setupHROperations() 가 루트만 다시 가리킨다.
// 예전처럼 클로저 안에 리스너를 두면 이동할 때마다 submit 핸들러가 한 벌씩 늘어
// 같은 폼이 여러 번 전송된다.
import { openPeoplePicker, peopleIndex, personEl } from './people-picker.js';
import { openDeptPicker } from './dept-picker.js';
import { appConfirm } from './confirm-dialog.js';
import { navigate } from './calendar-spa.js';

let root = null;            // 현재 #hrOperations
let wired = false;          // 문서·창 리스너 등록 여부 (한 번만)
let labels = {};
const baseline = new WeakMap();
let busy = false, navigating = false;
let taskKeySequence = 0;
// 인사 모달 (2026-09-29 인사 화면 일관성 라운드, 스펙 §5.2.1) — 아코디언 대신 쓰는
// partials/hr-modal.html 의 .modal-overlay.hr-modal. 열 때 main(#hrOperations) 끝으로 옮겨
// 카드·표의 쌓임 맥락을 벗어나고, 닫거나 저장하면 자리표(주석)로 되돌린다 — 저장 뒤의
// 카드 교체(navigate swap '#카드')가 그 카드 안에서 모달의 짝을 찾도록.
let openModal = null;               // {overlay, placeholder, opener}
const autoOpened = new Set();       // 주소 + id — 저장 뒤 같은 주소를 다시 받아도 또 열지 않는다

const actionURL = form => new URL(form.getAttribute('action') || location.href, location.href).href;
const serialize = form => JSON.stringify([...new FormData(form)].filter(([key]) => key !== 'if_version').map(([key,value]) => [key,value instanceof File ? (value.name === "" && value.size === 0 ? "" : {name:value.name,size:value.size,type:value.type,lastModified:value.lastModified}) : value]));
const forms = () => (root ? [...root.querySelectorAll('[data-hr-form]')] : []);
const dirty = form => baseline.get(form) !== serialize(form);
// 이 화면의 카드 집합 지문 (BE22). 서버가 #hrCardSet 의 data-cards 에 [data-hr-card]
// id 들을 채우고, 그 칸 자체가 [data-hr-sync][id] 라 카드 단위 교체에 함께 갈린다.
// 섬이 없는 옛 마크업은 언제나 null 이므로 비교가 늘 같아 폴백이 돌지 않는다.
const cardSet = () => (root ? (root.querySelector('#hrCardSet')?.dataset.cards ?? null) : null);

const syncTemplateEditor = form => {
    if (!form?.matches('[data-hr-template-editor]')) return;
    const rows = [...form.querySelectorAll('[data-hr-row]')];
    for (const row of rows) {
        const key = row.querySelector('[data-hr-task-key]');
        if (!key.value) key.value = globalThis.crypto?.randomUUID?.() || ('task-' + Date.now() + '-' + (++taskKeySequence));
    }
    for (const row of rows) {
        const select = row.querySelector('[data-hr-task-dependencies]');
        const selected = new Set([...select.selectedOptions].map(o => o.value));
        select.name = 'depends_' + row.querySelector('[data-hr-task-key]').value;
        select.replaceChildren(...rows.filter(r => r !== row).map((r, i) => {
            const option = new Option(r.querySelector('[data-hr-task-title]').value || String(i + 1), r.querySelector('[data-hr-task-key]').value);
            option.selected = selected.has(option.value); return option;
        }));
    }
};

const bind = () => {
    if (!root) return;
    forms().forEach(form => {
        syncTemplateEditor(form);
        if (!baseline.has(form)) baseline.set(form, serialize(form));
    });
    // Person pickers live in every [data-hr-form], but also in plain GET
    // filter forms (hr/time.html, hr/leave.html) that carry no
    // data-hr-form — so this sweeps the whole workspace, not just forms().
    root.querySelectorAll('[data-hr-person]').forEach(field => {
        if (field.dataset.pickerReady) return;
        field.dataset.pickerReady = '1';
        const name = field.dataset.hrPerson, multi = field.dataset.multi === '1';
        const allow = field.dataset.allow ? new Set(field.dataset.allow.split(',').filter(Boolean)) : null;
        const button = field.querySelector('.hr-person-btn'), view = field.querySelector('.hr-person-view');
        const values = () => [...field.querySelectorAll('[data-hr-person-value]')].map(i => i.value).filter(Boolean);
        const write = ids => {
            field.querySelectorAll('[data-hr-person-value]').forEach(i => i.remove());
            const list = ids.length ? ids : [''];
            for (const id of list) { const input = document.createElement('input'); input.type = 'hidden'; input.name = name; input.value = id; input.dataset.hrPersonValue = ''; field.append(input); }
            const idx = peopleIndex();
            view.replaceChildren(...ids.map(id => idx.byId[id] ? personEl(idx.byId[id], 'xs') : Object.assign(document.createElement('span'), { textContent: id })));
            if (!ids.length) { const empty = document.createElement('span'); empty.className = 'text-muted'; empty.textContent = labels.pick; view.append(empty); }
            field.dispatchEvent(new Event('change', { bubbles: true }));
        };
        button.addEventListener('click', async () => {
            button.disabled = true;
            try {
                const index = peopleIndex(); await index.ready;
                const opts = { values: values(), multi, allowNone: field.dataset.required !== '1', allowMe: true, dept: field.dataset.dept || '', labels: labels.people, onChange: write };
                if (allow) opts.exclude = index.list.filter(p => !allow.has(p.id)).map(p => p.id);
                openPeoplePicker(button, opts);
            } finally { button.disabled = false; }
        });
    });
};

const modalForms = overlay => [...overlay.querySelectorAll('[data-hr-form]')];
const firstField = overlay => overlay.querySelector('input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), .hr-person-btn');
function showModal(overlay, opener) {
    if (!overlay || !overlay.matches('[data-hr-modal-root]') || openModal?.overlay === overlay) return;
    if (openModal) hideModal();
    const placeholder = document.createComment('hr-modal');
    overlay.before(placeholder);
    (root || document.body).append(overlay);
    const err = overlay.querySelector('.hr-modal-error');
    if (err) { err.hidden = true; err.textContent = ''; }
    overlay.classList.add('show');
    openModal = { overlay, placeholder, opener: opener || null };
    document.documentElement.classList.add('hr-modal-open');
    // .modal-overlay 의 transition 이 visibility 까지 늦추므로 한 프레임 뒤에 포커스한다.
    requestAnimationFrame(() => requestAnimationFrame(() => firstField(overlay)?.focus({ preventScroll: true })));
}
function hideModal() {
    const m = openModal;
    if (!m) return;
    openModal = null;
    m.overlay.classList.remove('show');
    if (m.placeholder.isConnected) m.placeholder.replaceWith(m.overlay); else m.overlay.remove();
    document.documentElement.classList.remove('hr-modal-open');
    if (m.opener?.isConnected) m.opener.focus({ preventScroll: true });
}
async function requestCloseModal() {
    const m = openModal;
    if (!m) return;
    const changed = modalForms(m.overlay).filter(dirty);
    if (changed.length) {
        const ok = await appConfirm({ message: labels.modalDiscard, okLabel: labels.modalDiscardOk, cancelLabel: labels.keepEditing, danger: true });
        if (!ok || openModal !== m) return;
        changed.forEach(f => { f.reset(); baseline.set(f, serialize(f)); });
    }
    hideModal();
}
const modalOf = el => (openModal && openModal.overlay.contains(el) ? openModal : null);
// 서버가 처음부터 열어 두던 폼(증명서 신청 ?subject= · 빈 입퇴사 업무 · #correction)은
// data-hr-modal-autoopen 또는 주소의 #id 로 연다 — 같은 주소에서는 한 번만.
const autoOpenModal = () => {
    if (!root || openModal) return;
    let target = root.querySelector('[data-hr-modal-autoopen]');
    if (!target && location.hash.length > 1) {
        try { target = root.querySelector(location.hash); } catch (_) { target = null; }
        if (target && !target.matches('[data-hr-modal-root]')) target = null;
    }
    if (!target) return;
    const key = location.pathname + location.search + '#' + target.id;
    if (autoOpened.has(key)) return;
    autoOpened.add(key);
    showModal(target, null);
};

const feedback = (message, error = false) => {
    const el = root && root.querySelector('#hrFeedback');
    if (!el) return;
    // preventScroll: 저장 뒤 카드만 갈아끼우는 지금(§6.4) 포커스가 화면 맨 위의
    // 피드백 줄로 스크롤을 끌어올리면 안 된다 — role=status 라 읽히기는 그대로다.
    el.hidden = false; el.classList.toggle('error', error); el.textContent = message; el.focus({ preventScroll: true });
};

// ── SPA 훅 (모듈 임포트 시 한 번) ───────────────────────────────────────────
// 아래 두 리스너는 인사 화면이 아닌 곳(/directory 처럼 #hrOperations 가 없는 페이지)
// 에서 들어와도 걸려 있어야 하므로 wireOnce 가 아니라 모듈 최상위에서 등록한다.
//
// SPA 이동은 페이지를 떠나지 않으므로 beforeunload 가 뜨지 않는다 — 같은 보호를
// calendar:before-navigate 로 다시 건다 (erp.js 와 같은 모양).
document.addEventListener('calendar:before-navigate', event => {
    if (!root || !forms().some(dirty)) return;
    event.preventDefault();
    appConfirm({ title: labels.leaveTitle, message: labels.leaveAsk, okLabel: labels.discard, cancelLabel: labels.cancel, danger: true })
        .then(ok => {
            if (!ok) return;
            forms().forEach(f => baseline.set(f, serialize(f))); // 버리기로 확인 — 다시 묻지 않는다
            navigate(event.detail.href, { force: true });
        });
});
// 제자리 교체가 끝나면 새 #hrOperations 에 다시 붙는다. main.js 의 지연 로드 한 줄과
// 같은 조건으로 상호작용 모듈(보드·패널)도 새 DOM 에 다시 붙인다.
document.addEventListener('calendar:navigated', () => {
    // 모바일 드로어는 전체 새로고침이 없어졌으니 스스로 닫아야 한다 (열린 채로 남으면
    // 도착한 화면이 드로어 뒤에 가린다). rail-tooltip.js 가 심어 둔 토글을 그대로 쓴다.
    const sidebar = document.querySelector('.hr-page #adminSidebar');
    if (sidebar?.classList.contains('mobile-open') && typeof window.toggleAdminSidebar === 'function') window.toggleAdminSidebar();
    setupHROperations();
    if (document.querySelector('#hrBoard,#hrTeamCal,#hrOrg,#hrRecList,#hrPanel')) import('./hr-interactive.js').then(m => m.setup());
});

// 문서·창 리스너: SPA 이동으로 setupHROperations() 가 여러 번 불려도 한 벌만 남는다.
function wireOnce() {
    if (wired) return;
    wired = true;
    // 모달: [data-hr-modal="<id>"] 가 연다, ×·「취소」([data-hr-modal-close])·바깥·Esc 가
    // 닫는다(고친 입력이 있으면 묻는다).
    document.addEventListener('click', event => {
        if (!(event.target instanceof Element)) return;
        const opener = event.target.closest('[data-hr-modal]');
        if (opener && root && root.contains(opener)) {
            event.preventDefault();
            const id = opener.dataset.hrModal;
            showModal(root.querySelector('#' + CSS.escape(id)) || document.getElementById(id), opener);
            return;
        }
        if (!openModal) return;
        if (event.target.closest('[data-hr-modal-close]') && openModal.overlay.contains(event.target)) { event.preventDefault(); requestCloseModal(); return; }
        if (event.target === openModal.overlay) requestCloseModal();
    });
    document.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !openModal || event.defaultPrevented) return;
        if (document.querySelector('.confirm-dialog-overlay') || document.querySelector('.pp-pop')) return; // 확인 창 · 사람 고르기가 먼저 닫힌다
        event.preventDefault();
        requestCloseModal();
    });
    // <details data-hr-menu> (구성원 행의 ⋯ 메뉴 등): 바깥 클릭·Esc 로 닫힌다.
    document.addEventListener('click', event => {
        if (!root) return;
        root.querySelectorAll('[data-hr-menu][open]').forEach(menu => {
            if (!menu.contains(event.target)) menu.open = false;
        });
    });
    document.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !root) return;
        const menu = root.querySelector('[data-hr-menu][open]');
        if (menu) { menu.open = false; menu.querySelector('summary')?.focus(); event.preventDefault(); }
    });
    // 행 메뉴는 overflow:auto 인 표 감싸개 안에 있어 아래쪽 행에서는 잘린다 — 열릴 때
    // 판을 summary 의 화면 좌표로 fixed 배치하고(아래가 모자라면 위로) 스크롤이면 닫는다.
    // toggle 은 버블링하지 않으므로 캡처로 받는다.
    document.addEventListener('toggle', event => {
        const menu = event.target;
        if (!root || !(menu instanceof HTMLDetailsElement) || !menu.hasAttribute('data-hr-menu') || !root.contains(menu)) return;
        const panel = menu.querySelector(':scope > div');
        if (!panel) return;
        if (!menu.open) { panel.style.cssText = ''; return; }
        const anchor = menu.querySelector('summary').getBoundingClientRect();
        panel.style.position = 'fixed'; panel.style.left = 'auto'; panel.style.right = Math.max(0, innerWidth - anchor.right) + 'px';
        panel.style.top = (anchor.bottom + 4) + 'px'; panel.style.bottom = 'auto';
        if (anchor.bottom + 4 + panel.getBoundingClientRect().height > innerHeight) { panel.style.top = 'auto'; panel.style.bottom = (innerHeight - anchor.top + 4) + 'px'; }
    }, true);
    document.addEventListener('scroll', () => {
        if (!root) return;
        root.querySelectorAll('[data-hr-menu][open]').forEach(menu => { menu.open = false; });
    }, true);
    // 머리의 주 액션 버튼(증명서 신청·자산 등록·겸직 등록·목표 만들기 …)과 빈
    // 상태의 CTA 는 같은 훅이다(§6.4): 그 화면의 <details> 를 열어 보이게 하고
    // 첫 입력에 포커스를 준다. JS 없이는 <details> 의 summary 가 그대로 남는다.
    document.addEventListener('click', event => {
        const button = event.target.closest('[data-open-details]');
        if (!button || !root || !root.contains(button)) return;
        const target = root.querySelector(button.dataset.openDetails);
        if (!target) return;
        if (target.matches('[data-hr-modal-root]')) { event.preventDefault(); showModal(target, button); return; }
        target.open = true;
        target.scrollIntoView({ block: 'start', behavior: 'smooth' });
        target.querySelector('input:not([type="hidden"]), select, textarea, .hr-person-btn')?.focus({ preventScroll: true });
    });
    // 구성원 표(/hr, IS-1): 줄 전체가 프로필이다. 예전에는 이름 링크의 ::after 가 줄을 덮었는데(tr 을
    // containing block 으로), tr 의 position:relative 를 따르지 않는 곳에서는 그 덮개가 표 감싸개 전체를
    // 덮었고, 줄 글자를 끌어 고를 수도 없었다. 이제는 줄의 빈 곳을 누르면 그 줄의 이름 링크를 누른 것으로
    // 친다 — 이동은 링크 자신이 한다(SPA 라우터 · back-link 규칙 그대로), 키보드 · 보조 기술의 대상도
    // 여전히 이름 링크 하나다. 링크 · 단추 · 입력 · ⋯ 메뉴 위의 클릭과, 글자를 끌어 고른 뒤의 클릭은
    // 건드리지 않는다. Ctrl/Cmd(와 Shift) 클릭 · 가운데 단추는 링크처럼 새 탭(창)으로 연다. 열린 ⋯ 메뉴를
    // 닫으려고 줄을 누른 것은 이동이 아니다(위의 메뉴 닫기가 먼저 돌므로 캡처 단계에서 미리 본다).
    let menuWasOpen = false;
    document.addEventListener('click', () => { menuWasOpen = !!root?.querySelector('[data-hr-menu][open]'); }, true);
    const rowLink = event => {
        if (!root || !(event.target instanceof Element)) return null;
        const row = event.target.closest('#hrPeople tr.hr-people-row');
        if (!row || !root.contains(row)) return null;
        if (event.target.closest('a, button, input, select, textarea, label, summary, details')) return null;
        // 끌어서 고른 글자가 있으면 그 click 은 고르기의 끝이다(그냥 누르면 mousedown 이 선택을 접는다).
        const selected = window.getSelection?.();
        if (selected && !selected.isCollapsed && selected.toString().trim()) return null;
        return row.querySelector('a.hr-person-link[href]');
    };
    document.addEventListener('click', event => {
        if (event.defaultPrevented || event.button !== 0 || menuWasOpen) return;
        const link = rowLink(event);
        if (!link) return;
        if (event.ctrlKey || event.metaKey || event.shiftKey) { window.open(link.href, '_blank', 'noopener'); return; }
        if (event.altKey) return;
        link.click();
    });
    document.addEventListener('auxclick', event => {
        if (event.button !== 1) return;
        const link = rowLink(event);
        if (link) window.open(link.href, '_blank', 'noopener');
    });
    // 구성원 목록 필터(§6.2): "적용" 없이 바뀌는 즉시 제출한다. requestSubmit() 이
    // 올리는 submit 을 calendar-spa 가 같은 경로 GET 폼으로 가로채 #hrPeople 만
    // 갈아끼우고, 검색 칸은 [data-spa-region] 안이라 입력 중에는 건드리지 않는다.
    let filterTimer = 0;
    const autofilter = target => (root && root.contains(target) ? target.closest('form[data-hr-autofilter]') : null);
    document.addEventListener('change', event => {
        const form = autofilter(event.target);
        if (form && event.target.matches('select, input[type="checkbox"]')) { clearTimeout(filterTimer); form.requestSubmit(); }
    });
    document.addEventListener('input', event => {
        const form = autofilter(event.target);
        if (!form || !event.target.matches('input[type="search"]')) return;
        clearTimeout(filterTimer);
        filterTimer = setTimeout(() => form.requestSubmit(), 350);
    });
    // 팀 보기 칩(§11, /hr/leave · /hr/time): 팀 캘린더와 같은 dept-picker 를 열고
    // 고른 부서를 ?dept= 로 얹는다. 「팀 보기 끄기」(id 빈 값)는 그 파라미터만 지우므로
    // 조회 월·대상자 같은 다른 필터는 그대로다. 서버가 부서를 코호트로 바꾼다.
    document.addEventListener('click', event => {
        if (!root || !(event.target instanceof Element)) return;
        const chip = event.target.closest('[data-hr-dept]');
        if (!chip || !root.contains(chip)) return;
        event.preventDefault();
        let tree = [];
        try { tree = JSON.parse(root.querySelector('#deptTree')?.textContent || '[]'); } catch (_) { tree = []; }
        openDeptPicker(chip, {
            tree,
            current: chip.dataset.hrDept || '',
            labels: labels.dept,
            onPick: id => {
                const url = new URL(location.href);
                if (id) url.searchParams.set('dept', id); else url.searchParams.delete('dept');
                navigate(url.pathname + url.search, { swap: true, keepScroll: true });
            },
        });
    });
    window.addEventListener('pageshow', () => { navigating = false; });
    document.addEventListener('click', event => {
        if (!root) return;
        const add = event.target.closest('[data-hr-add-row]');
        if (add && root.contains(add)) { const template = root.querySelector('#' + add.dataset.hrAddRow); if (template) { template.before(template.content.cloneNode(true)); bind(); } }
        const remove = event.target.closest('[data-hr-remove-row]');
        if (remove && root.contains(remove)) { remove.closest('[data-hr-row]')?.remove(); bind(); }
    });
    document.addEventListener('input', event => { if (root && root.contains(event.target) && event.target.matches('[data-hr-task-title]')) syncTemplateEditor(event.target.form); });
    document.addEventListener('change', event => { const select = event.target; if (root && root.contains(select) && select.matches('[data-hr-workflow-template]') && select.selectedOptions[0]?.dataset.kind) select.form.elements.kind.value = select.selectedOptions[0].dataset.kind; });
    window.addEventListener('beforeunload', event => {
        if (forms().some(dirty)) { event.preventDefault(); event.returnValue = ''; }
    });
    document.addEventListener('submit', async event => {
        const form = event.target;
        if (!root || !root.contains(form) || !form.matches('[data-hr-form]')) return;
        // data-confirm 폼(사유 없는 취소·삭제 — 확인 창, 2026-09-29): confirm-dialog.js 가 먼저
        // 이 제출을 멈추고 확인 뒤 다시 보낸다. 멈춘 제출은 여기서도 보내지 않는다.
        if (event.defaultPrevented) return;
        event.preventDefault();
        if (busy || navigating) return;
        // Closing a workflow would discard other task edits; leave them available to save first.
        const isCreation = !form.elements.if_version || form.hasAttribute("data-hr-create");
        const isClosing = form.hasAttribute('data-hr-closing') || (!actionURL(form).endsWith('/tasks') && form.elements.status);
        const others = forms().filter(f => f !== form && dirty(f));
        if ((isClosing || isCreation) && others.length) { feedback(labels.unsaved, true); return; }
        busy = true;
        const buttons = [...root.querySelectorAll('button:not([type="button"]):not([type="reset"])')];
        const disabled = buttons.map(b => b.disabled);
        const multipart = form.enctype === 'multipart/form-data';
        const body = multipart ? new FormData(form) : new URLSearchParams(new FormData(form));
        if (event.submitter?.name) body.set(event.submitter.name, event.submitter.value);
        const submitted = serialize(form);
        buttons.forEach(b => b.disabled = true);
        root.setAttribute('aria-busy', 'true');
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), multipart ? 120000 : 15000);
        let persisted = false;
        try {
            const response = await fetch(actionURL(form), { method: 'POST', body, signal: controller.signal, headers: { 'Accept': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '' } });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.message || labels.failed);
            persisted = true;
            baseline.set(form, submitted);
            if (modalOf(form)) hideModal(); // 제자리로 돌려놓은 뒤 교체한다 — 카드 교체가 짝을 찾는다
            if (result.url) { navigating = true; location.assign(result.url); return; }
            // Advance only to the version acknowledged by our mutation, never to a background read.
            forms().forEach(f => { if (f.elements.if_version && (f.dataset.hrVersionScope || 'record') === (form.dataset.hrVersionScope || 'record')) f.elements.if_version.value = result.version; });
            // 저장 뒤 다시 그리기 (BE22, 2026-09-20): calendar-spa 의 선택자 교체
            // navigate(url, {swap: '#카드, [data-hr-sync][id]'}) 가 한 길이다 — 폼이 있는
            // 카드([data-hr-card]#id)와 파생 표시 블록([data-hr-sync][id]: 절차 진행률·
            // 프로필 사실·업무 상태)만 새 문서의 같은 id 요소로 바꾸므로 다른 아코디언의
            // 열림 상태와 본문 스크롤이 그대로다 (스펙 §6.4, 원칙 7). 카드가 생기거나
            // 사라지는 저장(만들기 폼 data-hr-create — 업무 추가·자산 등록·겸직 등록 —
            // 과 닫기 폼)과 카드 표식이 없는 옛 마크업은 페이지 통째 교체(swap:true)이고,
            // 새 문서에 그 카드가 없으면 navigate 가 스스로 통째 교체로 떨어진다.
            const card = form.closest('[data-hr-card]');
            let cardOnly = !!(card && card.id && !isCreation && !isClosing);
            const wasOpen = cardOnly && card.matches('details') ? card.open : null;
            // 저장 중에 타이핑한 입력(방금 낸 폼 포함)과 갈아끼워지는 범위 안 다른 편집기의
            // 미저장 입력은 새 마크업의 짝 폼 자리에 옛 폼 노드를 그대로 되돌려 보존한다.
            const keep = forms().filter(f => dirty(f) && (!cardOnly || card.contains(f)));
            const cardsBefore = cardOnly ? cardSet() : null;
            await navigate(location.href, cardOnly ? { swap: '#' + CSS.escape(card.id) + ', [data-hr-sync][id]' } : { swap: true, replace: true, keepScroll: true });
            if (!root) throw new Error(labels.saved);
            // 카드 집합이 바뀌었으면 통째 교체 (BE22 의 폴백): 선택자 교체는 두 문서에
            // 모두 있는 카드만 갈아끼우므로, 형제 카드가 생기거나 사라진 저장은 저장한
            // 카드만 새것이고 나머지는 옛 화면인 상태를 남긴다. #hrCardSet 은 그 자체가
            // [data-hr-sync][id] 라 방금 새 문서의 것으로 바뀌었으니 값만 비교하면 된다.
            if (cardOnly && cardSet() !== cardsBefore) {
                cardOnly = false;
                await navigate(location.href, { swap: true, replace: true, keepScroll: true });
                if (!root) throw new Error(labels.saved);
            }
            const scope = cardOnly ? root.querySelector('#' + CSS.escape(card.id)) : root;
            if (scope) {
                if (wasOpen !== null && scope.matches('details')) scope.open = wasOpen; // 방금 저장한 카드의 접힘도 그대로
                keep.forEach(old => {
                    const id = old.elements.task_id?.value;
                    const twin = [...scope.querySelectorAll('[data-hr-form]')].find(f => f.elements.task_id?.value === id && actionURL(f) === actionURL(old) && f.elements.scope?.value === old.elements.scope?.value && f.elements.action_id?.value === old.elements.action_id?.value && f.elements.action?.value === old.elements.action?.value && f.elements.status?.value === old.elements.status?.value);
                    if (twin) { const details = twin.closest('details'); twin.replaceWith(old); if (details) details.open = true; }
                });
            }
            // 다른 업무 카드의 「완료」 선택지는 방금 저장한 업무(선행 업무·자산 반납)에
            // 달려 있다 — 폼은 건드리지 않고, 같이 갈아끼워진 상태 블록(data-blocked)을
            // 읽어 그 option 의 disabled 만 맞춘다.
            root.querySelectorAll('[data-hr-form] select[name="status"] option[value="done"]').forEach(option => {
                const state = option.closest('[data-hr-card]')?.querySelector('.hr-task-state[data-hr-sync]');
                if (state) option.disabled = state.hasAttribute('data-blocked');
            });
            bind();
            feedback(labels.saved);
        } catch (error) {
            const message = persisted ? labels.saved : (error.name === 'AbortError' ? labels.failed : error.message);
            const inModal = !persisted && modalOf(form);
            const box = inModal ? inModal.overlay.querySelector('.hr-modal-error') : null;
            if (box) { box.hidden = false; box.textContent = message; } else feedback(message, !persisted);
        } finally {
            clearTimeout(timeout);
            if (!navigating) { busy = false; if (root) root.removeAttribute('aria-busy'); buttons.forEach((b, i) => { b.disabled = disabled[i]; }); }
        }
    });
}

export function setupHROperations() {
    document.querySelectorAll('[data-hr-copy-code]').forEach(button => {
        if (button.dataset.ready) return;
        button.dataset.ready = '1';
        button.addEventListener('click', async () => {
            const input = document.getElementById(button.dataset.hrCopyCode);
            if (!input) return;
            try {
                if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
                await navigator.clipboard.writeText(input.value);
                button.textContent = button.dataset.hrCopied;
            } catch {
                input.focus(); input.select();
                button.textContent = button.dataset.hrCopyFallback;
            }
        });
    });
    document.querySelectorAll('[data-hr-public-form]').forEach(form => {
        if (form.dataset.ready) return;
        form.dataset.ready = '1';
        form.addEventListener('submit', event => {
            if (form.dataset.submitting) { event.preventDefault(); return; }
            form.dataset.submitting = '1';
            form.querySelectorAll('button').forEach(button => { button.disabled = true; });
        });
        window.addEventListener('pageshow', () => { delete form.dataset.submitting; form.querySelectorAll('button').forEach(button => { button.disabled = false; }); });
    });
    const fresh = document.getElementById('hrOperations');
    if (!fresh) { root = null; return; }              // 인사 화면이 아니다 (또는 떠났다)
    if (fresh === root && fresh.dataset.ready) return; // 같은 루트에 이미 붙어 있다
    root = fresh;
    root.dataset.ready = '1';
    if (openModal && !openModal.overlay.isConnected) { openModal = null; document.documentElement.classList.remove('hr-modal-open'); }
    const el = document.getElementById('hrLabels');
    try { labels = el ? JSON.parse(el.textContent || '{}') : {}; } catch (_) { labels = {}; }
    wireOnce();
    bind();
    autoOpenModal();
}
